import {
  callSaradiumBackend,
} from "../../lib/appwrite";

import type {
  CreateEventCircleInput,
  EventCircle,
  EventCircleDashboard,
  InviteeResponse,
  UpdateEventCircleInput,
} from "./eventCircle.types";

function normaliseResponse(
  value: unknown,
): EventCircle["responseStatus"] {
  if (
    value === "HOST" ||
    value === "GOING" ||
    value === "MAYBE" ||
    value === "BUSY" ||
    value === "NOT_GOING"
  ) {
    return value;
  }

  return "AWAITING";
}

function normaliseCircle(
  value: Partial<EventCircle>,
): EventCircle {
  return {
    circleId: String(value.circleId || ""),
    title: String(value.title || ""),
    description: String(value.description || ""),
    location: String(value.location || ""),
    startsAt: Number(value.startsAt || 0),
    endsAt: Number(value.endsAt || 0),

    visibility:
      value.visibility === "PUBLIC"
        ? "PUBLIC"
        : "PRIVATE",

    status:
      value.status === "CANCELLED"
        ? "CANCELLED"
        : "ACTIVE",

    editCount: Number(value.editCount || 0),
    updatedAt: Number(value.updatedAt || 0),

    isHost: Boolean(value.isHost),

    responseStatus: normaliseResponse(
      value.responseStatus,
    ),

    shareable: Boolean(value.shareable),

    token:
      typeof value.token === "string"
        ? value.token
        : undefined,

    invitationId:
      typeof value.invitationId === "string"
        ? value.invitationId
        : undefined,
  };
}

function recipientsFor(
  input: CreateEventCircleInput,
) {
  const recipients = (input.recipients ?? [])
    .map((recipient) => ({
      recipientType: recipient.recipientType,
      recipient: recipient.recipient.trim(),
    }))
    .filter((recipient) => recipient.recipient);

  const legacyRecipient =
    input.recipient?.trim() ?? "";

  if (
    legacyRecipient &&
    !recipients.some(
      (recipient) =>
        recipient.recipientType ===
          input.recipientType &&
        recipient.recipient.toLowerCase() ===
          legacyRecipient.toLowerCase(),
    )
  ) {
    recipients.push({
      recipientType:
        input.recipientType || "USERNAME",
      recipient: legacyRecipient,
    });
  }

  return recipients;
}

export async function listMyEventCircles() {
  const result =
    await callSaradiumBackend<{
      circles?: Partial<EventCircle>[];
    }>({
      action: "listMyEventCircles",
    });

  return (result.circles || []).map(
    normaliseCircle,
  );
}

export async function createEventCircle(
  input: CreateEventCircleInput,
) {
  const recipients = recipientsFor(input);

  const result =
    await callSaradiumBackend<
      Partial<EventCircle>
    >({
      action: "createEventCircleInvitation",

      title: input.title.trim(),
      description: input.description.trim(),
      location: input.location.trim(),
      startsAt: input.startsAt,
      endsAt: input.endsAt,
      visibility: input.visibility,

      recipientType:
        recipients[0]?.recipientType ||
        input.recipientType ||
        "USERNAME",

      recipient:
        recipients[0]?.recipient || "",

      recipients,

      allDay: Boolean(input.allDay),

      timeZone:
        input.timeZone ||
        Intl.DateTimeFormat()
          .resolvedOptions()
          .timeZone,

      addToCountdown: Boolean(
        input.addToCountdown,
      ),
    });

  return normaliseCircle(result);
}

export async function getEventCircleInvitation(
  token: string,
) {
  const result =
    await callSaradiumBackend<
      Partial<EventCircle>
    >({
      action: "getEventCircleInvitation",
      token,
    });

  return normaliseCircle(result);
}

export async function respondToEventCircle(
  token: string,
  status: InviteeResponse,
) {
  const result =
    await callSaradiumBackend<
      Partial<EventCircle>
    >({
      action: "respondToEventCircle",
      token,
      status,
    });

  return normaliseCircle(result);
}

export async function updateEventCircle(
  input: UpdateEventCircleInput,
) {
  const result =
    await callSaradiumBackend<
      Partial<EventCircle>
    >({
      action: "updateEventCircle",
      ...input,
    });

  return normaliseCircle(result);
}

export async function cancelEventCircle(
  circleId: string,
) {
  const result =
    await callSaradiumBackend<
      Partial<EventCircle>
    >({
      action: "cancelEventCircle",
      circleId,
    });

  return normaliseCircle(result);
}

export async function getEventCircleDashboard(
  circleId: string,
) {
  const result =
    await callSaradiumBackend<
      Partial<EventCircleDashboard>
    >({
      action: "getEventCircleDashboard",
      circleId,
    });

  return {
    ...normaliseCircle(result),
    going: Number(result.going || 0),
    maybe: Number(result.maybe || 0),
    busy: Number(result.busy || 0),
    notGoing: Number(result.notGoing || 0),
    awaiting: Number(result.awaiting || 0),
  };
}